export function createElement(tagname: string, id: string = '', className: string = ''): HTMLElement {
    let element = document.createElement(tagname);

    if (id) {
        element.id = id;
    }

    addClass(element, className);

    return element;
}

export function addClass(element: HTMLElement | null, className: string) {
    if (className === '' || !element) {
        return;
    }

    let classNames = className.trim().split(' ');
    classNames = classNames.filter((val) => val.trim() !== '');

    element.classList.add(...classNames);
}

export function removeClass(element: HTMLElement | null, className: string) {
    if (className === '' || !element) {
        return;
    }

    let classNames = className.trim().split(' ');
    classNames = classNames.filter((val) => val.trim() !== '');

    element.classList.remove(...classNames);
}

export function hasClass(element: HTMLElement, className: string) {
    return element.classList.contains(className);
}

export function getOffsetParent(element: HTMLElement): HTMLElement | null {
    return element.offsetParent as HTMLElement | null;
}

export function toPixel(value: string): number {
    return parseInt(value, 10) || 0;
}

function transform(element: HTMLElement, value: string) {
    element.style.setProperty('transform', value);
    element.style.setProperty('-moz-transform', value);
    element.style.setProperty('-ms-transform', value);
    element.style.setProperty('-webkit-transform', value);
    element.style.setProperty('-o-transform', value);
}

function transformOrigin(element: HTMLElement, value: string) {
    element.style.setProperty('transform-origin', value);
    element.style.setProperty('-moz-transform-origin', value);
    element.style.setProperty('-ms-transform-origin', value);
    element.style.setProperty('-webkit-transform-origin', value);
    element.style.setProperty('-o-transform-origin', value);
}

// eslint-disable-next-line max-params
export function scaleElement(
    element: HTMLElement,
    scaleX: number,
    scaleY: number,
    originX: number = 0,
    originY: number = 0
) {
    // eslint-disable-next-line no-param-reassign
    scaleX = scaleX || 1;
    // eslint-disable-next-line no-param-reassign
    scaleY = scaleY || 1;

    // eslint-disable-next-line no-param-reassign
    originX = originX || 0;
    // eslint-disable-next-line no-param-reassign
    originY = originY || 0;

    let scale = `scale(${scaleX}, ${scaleY})`;
    let origin = `${originX} ${originY}`;

    transform(element, scale);

    transformOrigin(element, origin);
}

export function escapeHtml(str: string) {
    let element = createElement('div');

    element.textContent = str;

    return element.innerHTML;
}

export function toRgbColor(hexColor: string): string | null {
    //If the hex value is in short syntax(#fff), the value must be extended to the long one.*/
    if (hexColor && hexColor.length < 6) {
        let index = 1;
        let hexParts = hexColor.split('');
        if (hexParts[0] !== '#') {
            index = 0;
        }

        for (index; index < hexParts.length; index++) {
            hexParts[index] = hexParts[index] + hexParts[index];
        }

        // eslint-disable-next-line no-param-reassign
        hexColor = hexParts.join('');
    }

    let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hexColor);

    return result ? parseInt(result[1], 16) + ', ' + parseInt(result[2], 16) + ', ' + parseInt(result[3], 16) : null;
}

export function isRgbColor(color: string): boolean {
    if (!color) {
        return false;
    }
    return color.indexOf(',') > -1 ? true : false;
}

export function getColorAlphaValue(color: string): number {
    if (color.toLowerCase() === 'transparent') {
        return 0;
    }
    if (!isRgbColor(color)) {
        return 1;
    }

    if (color.indexOf('#') !== -1) {
        let rgbColor = toRgbColor(color);

        if (rgbColor === null) {
            return 1;
        }

        // eslint-disable-next-line no-param-reassign
        color = rgbColor;
    }

    let colorComponents = color.split(',').map(function(c) {
        return c.trim();
    });
    let alpha =
        colorComponents.length === 4
            ? parseFloat((parseFloat(colorComponents[3].replace(/[()]/g, '')) / 255).toFixed(2))
            : 1;
    return alpha;
}

export function appendHtml(target: HTMLElement, str: string) {
    let element = createElement('div');

    element.innerHTML = str;

    while (element.childNodes.length) {
        target.appendChild(element.childNodes[0]);
    }
}

export function prependHtml(target: HTMLElement, str: string) {
    let element = createElement('div');

    element.innerHTML = str;

    while (element.childNodes.length) {
        target.prepend(element.childNodes[element.childNodes.length - 1]);
    }
}

export function findElement(element: HTMLElement | null, selector: string): HTMLElement | null {
    if (element === null) {
        return null;
    }

    return element.querySelector(selector) as HTMLElement;
}

export function getElementAttributeValue(element: HTMLElement | null, attributeName: string): string {
    if (!element || !element.attributes) {
        return '';
    }

    return (element.attributes as any)[attributeName]?.value || '';
}

export function getElementScrollParent(element: HTMLElement): HTMLElement | null {
    let parent = element.parentElement;

    if (!parent) {
        return null;
    }

    if (parent.clientHeight != parent.scrollHeight) {
        return parent;
    }

    return getElementScrollParent(parent);
}

export function keepElementInView(element: HTMLElement | null) {
    if (!element) {
        return;
    }

    let scrollParent = getElementScrollParent(element);

    if (!scrollParent) {
        return;
    }

    let elementTop = element.offsetTop;
    let elementBottom = elementTop + element.offsetHeight;
    let parentTop = scrollParent.scrollTop;
    let parentBottom = parentTop + scrollParent.offsetHeight;

    if (elementTop < scrollParent.scrollTop) {
        scrollParent.scrollTop = elementTop;
    } else if (elementBottom > parentBottom) {
        scrollParent.scrollTop += elementBottom - parentBottom;
    }
}
